import * as React from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/styles";
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
} from "@material-ui/core";
import moment from "moment";

import {
  DestinationDefinition,
  destinationKeyToSlugMap,
  slugForDestination,
  warehouseDestinations,
} from "../destinations/destinations";
import { LoadedDestinationInfo } from "../types/global";
import { CohortDestinationInfo } from "./types";
import {
  EmptyBoxIcon,
  RefreshIcon,
} from "../components/icons/themes/windows10";
import Empty from "../components/icons/svg/logos/Empty";
import { useHistory } from "react-router";
import { useAllSaasDestinations } from "../hooks/useAllSaasDestinations";
import { DestinationSlug } from "../destinations/types";
import { useDestinationMetadataManager } from "../hooks/useDestinationMetadataManager";

const useCohortDestinationsStyles = makeStyles((theme: Theme) => ({
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
  },
  smallIcon: {
    display: "flex",
    marginRight: theme.spacing(1) / 2,
  },
  content: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  backfillButton: {
    paddingRight: 10,
    paddingLeft: 10,
  },
  destinationSettingsButton: {
    display: "flex",
    alignItems: "center",
    marginTop: 0,
    "&:hover": {
      cursor: "pointer",
    },
  },
  colorBlue: {
    color: "blue",
  },
  bold: {
    fontWeight: 550,
  },
  noMarginTop: {
    marginTop: 0,
  },
  destinationInfo: {
    display: "flex",
    alignItems: "center",
  },
  destinationName: {
    color: "#444",
    fontSize: 16,
    verticalAlign: "center",
    marginLeft: theme.spacing(2),
  },
}));

interface CohortDestinationsPanelProps {
  destinations: LoadedDestinationInfo[];
  enabledDests: CohortDestinationInfo;
  onToggleDestination: (destID: string) => void;
  triggerCohortDestResync: (destID: string) => void;
}

const CohortDestinationsPanelEmptyState: React.FC = () => {
  const history = useHistory();

  return (
    <div className="w-full bg-white flex px-10 py-4 items-center text-md text-navy-700">
      <EmptyBoxIcon className="mr-2" />
      You have no cohort destinations connected. Add one{" "}
      <span
        className="ml-1 underline hover:text-navy-500 cursor-pointer"
        onClick={() => history.push("/destinations/cohorting/")}
      >
        here
      </span>
      .
    </div>
  );
};

export const CohortDestinationsPanel: React.FC<CohortDestinationsPanelProps> = ({
  destinations,
  enabledDests,
  onToggleDestination,
  triggerCohortDestResync,
}) => {
  const classes = useCohortDestinationsStyles();
  const destinationMetaManager = useDestinationMetadataManager();
  const cohortingDestinations = destinations.filter((dest) => {
    return (
      dest.enabled &&
      destinationMetaManager.destinationSupportsCohorts(
        slugForDestination(dest)
      )
    );
  });
  const allSaasDestinations = useAllSaasDestinations();
  const allDestinations: {
    [destSlug: DestinationSlug]: DestinationDefinition;
  } = { ...warehouseDestinations, ...allSaasDestinations };

  const renderedDestinations = cohortingDestinations.map((dest) => {
    const destinationSlug = slugForDestination(dest);
    const destinationDefinition = allDestinations[destinationSlug];
    const DestinationIcon = destinationDefinition.logoSquare;
    const destinationData = enabledDests[dest.id];

    let isEnabled = !!destinationData;
    let isSyncing: boolean | null = false,
      syncedTo: number | null = null;

    if (destinationData) {
      isSyncing = destinationData.is_syncing;
      syncedTo = destinationData.synced_to;
    }

    return (
      <TableRow key={dest.id}>
        <TableCell>
          <div className={clsx(classes.destinationInfo, "justify-between")}>
            <span>
              {DestinationIcon && <DestinationIcon height={34} width={34} />}
              <span className={clsx(classes.destinationName, "mr-1")}>
                {destinationDefinition.name}
              </span>
            </span>
            {isEnabled && enabledDests[dest.id].enabled && (
              <div className={clsx({ "animate-pulse": isSyncing })}>
                {/* Sync status/date */}
                {isSyncing ? (
                  <span className="text-gray-600">Syncing Now</span>
                ) : (
                  <>
                    {syncedTo && (
                      <Tooltip
                        title={`Last synced at ${moment(syncedTo!).utc(true)}`}
                      >
                        <span className="text-gray-600">
                          Last synced: {moment(syncedTo!).fromNow()}
                        </span>
                      </Tooltip>
                    )}
                  </>
                )}

                {/* Trigger resync button */}
                <Tooltip title={"Re-sync "}>
                  <span
                    style={{ top: -1 }}
                    onClick={() => {
                      triggerCohortDestResync(dest.id);
                    }}
                    className="ml-2 cursor-pointer relative"
                  >
                    <RefreshIcon
                      className={clsx({ "animate-spin": isSyncing })}
                    />
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
        </TableCell>

        <TableCell align="center" style={{ width: "15%" }}>
          <Switch
            checked={!!enabledDests[dest.id] && enabledDests[dest.id].enabled}
            color="secondary"
            name="enable-destination-for-cohort"
            onChange={() => onToggleDestination(dest.id)}
            value={!!enabledDests[dest.id] && enabledDests[dest.id].enabled}
          />
        </TableCell>
      </TableRow>
    );
  });

  return (
    <Card className="mb-20 mt-5">
      <Divider />
      <CardContent className={classes.content}>
        {cohortingDestinations.length ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Destination</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderedDestinations}</TableBody>
          </Table>
        ) : (
          <CohortDestinationsPanelEmptyState />
        )}
      </CardContent>
    </Card>
  );
};
